import { groupBy } from "lodash";
import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GroupTitleTemplate } from "@ryde/controls";
import type { GenericUploadRowConflictError } from "@ryde/shared";

import type { IGenericUploadPhoneConflict } from "../generic-upload.models";
import { GenericUploadPhoneConflictsSectionStyled } from "../generic-upload.styled";
import { PhoneConflictsTable } from "./components";
import { GenericUploadPhoneConflictsContext } from "./contexts";

interface IGenericUploadPhoneConflictsProps {
  conflicts: IGenericUploadPhoneConflict[];
  skippedDataIndexes: number[];
  unresolvedRowsCount: number;
  isExistingCheckFailed: boolean;
  onToggleSkippedRow: (dataIndex: number) => void;
}

export const GenericUploadPhoneConflicts: FC<
  IGenericUploadPhoneConflictsProps
> = ({
  conflicts,
  skippedDataIndexes,
  unresolvedRowsCount,
  isExistingCheckFailed,
  onToggleSkippedRow,
}) => {
  const { t } = useTranslation();

  const conflictsByType = useMemo(
    () => groupBy(conflicts, "errorType"),
    [conflicts],
  );

  return (
    <GenericUploadPhoneConflictsSectionStyled>
      <GroupTitleTemplate
        name="phone-conflicts"
        formLabel={t("genericUpload.phoneConflicts.title")}
      />

      {isExistingCheckFailed && (
        <p className="phone-conflicts__warning" role="alert">
          {t("genericUpload.phoneConflicts.checkFailed")}
        </p>
      )}

      {conflicts.length > 0 && (
        <p className="phone-conflicts__hint" role="status" aria-live="polite">
          {unresolvedRowsCount > 0
            ? t("genericUpload.phoneConflicts.resolveHint", {
                count: unresolvedRowsCount,
              })
            : t("genericUpload.phoneConflicts.allResolved")}
        </p>
      )}

      {(Object.keys(conflictsByType) as GenericUploadRowConflictError[]).map(
        errorType => (
          <GenericUploadPhoneConflictsContext.Provider
            key={errorType}
            value={{
              conflicts: conflictsByType[errorType],
              skippedDataIndexes,
              onToggleSkippedRow,
            }}
          >
            <PhoneConflictsTable errorType={errorType} />
          </GenericUploadPhoneConflictsContext.Provider>
        ),
      )}
    </GenericUploadPhoneConflictsSectionStyled>
  );
};
